<template>
  <div class="fill-height" v-if="!$store.state.hidden">
    <v-sheet :rounded="isSmallerDisplay() ? '' : 'lg'" class="pa-2 pb-0">
      <v-card-title class="text-caption ma-0 pl-0 pt-0">
        {{ $t("message.view_ibjjf_competitors") }}
        <v-spacer></v-spacer>
      </v-card-title>
      <v-container class="mt-0 pt-0">
        <v-row class="mt-0 pt-0 pb-0 mb-0">
          <v-col cols="12">
            <v-autocomplete clearable :search-input.sync="searchTerm" :loading="searching"
              :no-data-text="$t('message.filter_competitor_no_data')" :label="$t('message.search_competitor')"
              @change="onSelectCompetitor" outlined dense :items="searchResults" item-text="name" item-value="_id"
              v-model="selectedCompetitorId">
            </v-autocomplete>
          </v-col>
        </v-row>
      </v-container>

      <v-container v-if="loadingProfile" class="text-center pa-6">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-container>

      <template v-if="profile && !loadingProfile">
        <v-container class="pt-0">
          <CompetitorStatsSummary :profile="profile" :active-tournaments="activeTournaments"
            :checking-active="checkingActive" :results="results">
          </CompetitorStatsSummary>
        </v-container>

        <v-container v-if="!profile.user" class="pt-0">
          <v-alert v-if="myClaim && !myClaim.verifiedOn" type="info" dense outlined>
            {{ $t("message.ibjjf_claim_pending") }}
          </v-alert>
          <v-alert v-else-if="myClaim && myClaim.verifiedOn && !myClaim.verified" type="error" dense outlined>
            {{ $t("message.ibjjf_claim_rejected") }} {{ myClaim.message }}
          </v-alert>
          <div v-if="!hasOtherClaim && (!myClaim || (myClaim.verifiedOn && !myClaim.verified))" class="text-center">
            <v-btn outlined small color="primary" @click="claimDialog = true">
              {{ $t("message.verify_ibjjf_profile") }}
            </v-btn>
          </div>
        </v-container>

        <v-data-table :headers="resultHeaders" :items="results" :items-per-page="10" class="elevation-0" dense>
          <template v-slot:item.medal="{ item }">
            <IconCompMedal v-if="item.medal" :color="themeColor(item.medal)"></IconCompMedal>
            <span v-else>-</span>
          </template>
          <template v-slot:item.tournamentDate="{ item }">
            {{ formatDate(item.tournamentDate) }}
          </template>
          <template v-slot:item.belt="{ item }">
            {{ [item.belt, item.age, item.weight, item.gender].filter(Boolean).join(" / ") }}
          </template>
        </v-data-table>

        <v-container class="pt-4">
          <div class="text-subtitle-2 mb-1">
            {{ $t("message.bjjmetrics_history") }}
          </div>
          <div v-if="!profile.bjjmetricsSlugChecked" class="text-caption grey--text">
            {{ $t("message.bjjmetrics_not_yet_checked") }}
          </div>
          <div v-else-if="!profile.bjjmetricsSlugFound" class="text-caption grey--text">
            {{ $t("message.bjjmetrics_not_found") }}
          </div>
        </v-container>
        <v-data-table v-if="profile.bjjmetricsHistory && profile.bjjmetricsHistory.length > 0"
          :headers="bjjmetricsHeaders" :items="profile.bjjmetricsHistory" :items-per-page="10" class="elevation-0"
          dense>
          <template v-slot:item.place="{ item }">
            <IconCompMedal v-if="item.place > 0 && item.place <= 3" :color="getMedalColor(item.place)"></IconCompMedal>
            <span v-else>{{ item.place || "-" }}</span>
          </template>
        </v-data-table>
      </template>
    </v-sheet>

    <v-dialog v-model="claimDialog" max-width="500px">
      <v-card outlined>
        <v-card-title>
          <div class="text-subtitle-1">{{ $t("message.verification_ibjjf_competitor") }}</div>
        </v-card-title>
        <v-card-text class="text-left">
          <input style="display: none" type="file" accept="*" @change="onClaimFileSelect"
            ref="claimFileUploadInput" />
          <div>
            <v-btn elevation="0" block style="margin-left: 5px" @click="claimFilePicker()" color="secondary" small>
              <PaperClipIcon class="hero-icon"></PaperClipIcon>{{ $t("message.upload_document") }}
            </v-btn>
            <span v-if="claimUploading"><v-progress-circular indeterminate
                color="terciary"></v-progress-circular></span><span v-else-if="claimFilename">
              <CheckCircleIcon class="hero-button" :color="themeColor('success')"></CheckCircleIcon>{{
                claimFilename }}
            </span>
          </div>
          <div class="supporting-text">
            {{ $t("message.verify_ibjjf_profile_explained") }}
          </div>
          <div class="supporting-text mt-1">
            {{ $t("message.verify_ibjjf_profile_name_match") }}
          </div>
        </v-card-text>
        <div v-if="claimUploading" class="mt-2">
          <v-progress-linear buffer-value="5" color="success" height="15" stream :value="claimUploadPercentage">
          </v-progress-linear>
        </div>
        <v-card-actions>
          <v-btn outlined :disabled="claimUploading || !claimUploadedFile" color="primary" elevation="0"
            @click="submitClaim()">{{ $t("message.verify") }}</v-btn>
          <v-btn elevation="0" @click="closeClaimDialog()">{{ $t("message.cancel") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import IBJJFService from "@/services/IBJJFService";
import IconCompMedal from "@/components/displays/icons/IconCompMedal";
import CompetitorStatsSummary from "@/components/displays/CompetitorStatsSummary";
import FileService3 from "@/services/FileService3";
import UserService from "@/services/UserService";

import { PaperClipIcon, CheckCircleIcon } from "@vue-hero-icons/outline";

export default {
  name: "ViewIBJJFCompetitors",
  components: {
    IconCompMedal,
    CompetitorStatsSummary,
    PaperClipIcon,
    CheckCircleIcon,
  },
  data() {
    return {
      searchTerm: null,
      searching: false,
      searchResults: [],
      selectedCompetitorId: null,
      suppressSearchWatch: false,
      loadingProfile: false,
      checkingActive: false,
      profile: null,
      results: [],
      activeTournaments: [],
      myClaim: null,
      hasOtherClaim: false,
      claimDialog: false,
      claimUploading: false,
      claimFilename: null,
      claimUploadedFile: null,
      claimUploadPercentage: 0,
      claimSelectedFile: null,
      resultHeaders: [
        { text: this.$t("message.tournament"), value: "tournamentName" },
        { text: this.$t("message.date"), value: "tournamentDate" },
        { text: this.$t("message.division"), value: "belt" },
        { text: this.$t("message.wins"), value: "wins" },
        { text: this.$t("message.losses"), value: "losses" },
        { text: this.$t("message.medal"), value: "medal" },
        { text: this.$t("message.affiliation"), value: "affiliationName" },
      ],
      bjjmetricsHeaders: [
        { text: this.$t("message.tournament"), value: "tournament" },
        { text: this.$t("message.division"), value: "division" },
        { text: this.$t("message.place"), value: "place" },
        { text: this.$t("message.affiliation"), value: "team" },
      ],
    };
  },
  watch: {
    searchTerm(term) {
      // v-autocomplete's search-input.sync fires this watcher on its own whenever we
      // set selectedCompetitorId programmatically (loadProfile), often passing through
      // an empty string first — without this guard that wipes searchResults right as
      // Vuetify tries to render the pre-selected item, leaving the box blank.
      if (this.suppressSearchWatch) return;
      if (!term) {
        this.searchResults = [];
        return;
      }
      this.searching = true;
      IBJJFService.searchAllCompetitors(term)
        .then((data) => {
          this.searching = false;
          this.searchResults = data.results;
        })
        .catch(() => {
          this.searching = false;
        });
    },
  },
  mounted() {
    if (this.$route.params.competitorId) {
      this.loadProfile(this.$route.params.competitorId);
    }
  },
  methods: {
    onSelectCompetitor(competitorId) {
      if (!competitorId) return;
      this.loadProfile(competitorId);
    },
    loadProfile(competitorId) {
      this.loadingProfile = true;
      this.checkingActive = true;
      this.profile = null;
      this.results = [];
      this.activeTournaments = [];
      IBJJFService.getCompetitorProfile(competitorId)
        .then((data) => {
          this.loadingProfile = false;
          this.checkingActive = false;
          this.profile = data.competitor;
          this.results = data.results;
          this.activeTournaments = data.activeTournaments || [];
          this.myClaim = data.myClaim || null;
          this.hasOtherClaim = data.hasOtherClaim || false;
          this.suppressSearchWatch = true;
          this.searchResults = [{ _id: data.competitor._id, name: data.competitor.name }];
          this.selectedCompetitorId = data.competitor._id;
          setTimeout(() => {
            this.suppressSearchWatch = false;
          }, 300);
        })
        .catch(() => {
          this.loadingProfile = false;
          this.checkingActive = false;
        });
    },
    formatDate(d) {
      if (!d) return "";
      return new Date(d).toLocaleDateString();
    },
    claimFilePicker() {
      this.claimUploading = false;
      this.claimUploadedFile = null;
      this.claimFilename = null;
      this.$refs.claimFileUploadInput.click();
    },
    onClaimFileSelect(event) {
      this.claimUploading = true;
      if (event.target.files[0]) {
        this.claimUploadedFile = null;
        this.claimSelectedFile = event.target.files[0];
        const size = event.target.files[0].size;
        const sizeMb = Math.floor(size / 1000000);
        if (sizeMb <= 15) {
          this.uploadClaimFile();
        } else {
          this.claimUploading = false;
          this.showAlert("error", this.$t("message.file_too_big"));
        }
      } else {
        this.claimUploading = false;
      }
    },
    async uploadClaimFile() {
      const formData = new FormData();
      this.claimFilename = this.claimSelectedFile.name;
      const vm = this;
      const millis = new Date().getTime();
      const imgExtension = this.claimSelectedFile.type.toLowerCase().split("/")[1];
      const blob = new Blob([this.claimSelectedFile], { type: this.claimSelectedFile.type });
      formData.append(
        "file",
        blob,
        "bjjlink-user-regular-uploaded-image-" + millis + "." + imgExtension
      );
      const response = await FileService3.uploadFile(formData, function (progressEvent) {
        const percent = parseInt(
          Math.round((progressEvent.loaded / progressEvent.total) * 100)
        );
        vm.claimUploadPercentage = percent >= 97 ? 97 : percent;
      });
      if (response && response.location) {
        setTimeout(function () {
          vm.claimUploadedFile = response.location;
          vm.claimUploading = false;
        }, 4000);
      } else {
        this.claimUploading = false;
        this.showAlert("error", this.$t("message.error"));
      }
    },
    submitClaim() {
      const formData = {
        type: "ibjjf_competitor",
        value: String(this.profile._id),
        fileName: this.claimFilename,
        fileUrl: this.claimUploadedFile,
      };
      this.claimUploading = true;
      UserService.initVerification(formData)
        .then(() => {
          this.showAlert("success", this.$t("message.submitted"));
          this.closeClaimDialog();
          this.loadProfile(this.profile._id);
        })
        .catch(() => {
          this.claimUploading = false;
          this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    closeClaimDialog() {
      this.claimDialog = false;
      this.claimUploading = false;
      this.claimUploadedFile = null;
      this.claimFilename = null;
      if (this.$refs.claimFileUploadInput) this.$refs.claimFileUploadInput.value = null;
    },
  },
};
</script>
